import mixpanel from "mixpanel-browser";
import { mapProductToTier, mapProductToTerm } from "./pricing";
import type { ProductPath } from "./fastspring";
import type { PageExperiment } from "./experiments";

export const initMixpanel = () => {
  mixpanel.init(import.meta.env.PUBLIC_MIXPANEL_API_TOKEN, {
    opt_out_tracking_by_default: false,
    ignore_dnt: true,
    persistence: "localStorage",
    debug: !import.meta.env.SITE,
    track_pageview: false,
  });
};

export const trackIdentity = (id: string, email?: string) => {
  mixpanel.identify(id);
  mixpanel.register({
    "Logged In": true,
  });
  if (email) {
    mixpanel.people.set({ $email: email });
  }
};

export const trackLogOut = () => {
  mixpanel.register({
    "Logged In": false,
  });
  mixpanel.reset();
};

export const initializeTracking = (id?: string, email?: string) => {
  if (mixpanel.has_opted_out_tracking({ persistence_type: "localStorage" })) {
    mixpanel.clear_opt_in_out_tracking({
      persistence_type: "localStorage",
    });
    mixpanel.opt_in_tracking({
      persistence_type: "localStorage",
    });
  }

  if (id) {
    trackIdentity(id, email);
  }
};

export const trackButtonPress = (name: string) => {
  mixpanel.track(name, {
    url: window.location.pathname,
  });
};

export const trackMixpanelEvent = (
  name: string,
  info: any = {},
  options: any = {},
  callback?: () => void,
) => {
  mixpanel.track(
    name,
    {
      url: window.location.pathname,
      ...info,
    },
    options,
    callback,
  );
};

export const setMixpanelUserPropertyOnce = (name: string, value: any) => {
  mixpanel.people.set_once({
    [name]: value,
  });
};

export const trackBuyButtonClick: {
  (
    productPath: ProductPath,
    licenseQty?: number,
    currentTier?: string,
    currentTerm?: string,
  ): void;
} = (productPath, licenseQty = 1, currentTier, currentTerm) => {
  const selectedTier = mapProductToTier(productPath);
  const selectedTerm = mapProductToTerm(productPath);
  trackMixpanelEvent("Web Pricing Click Plan Buy Now", {
    "Subscription Tier Selected": selectedTier,
    "Subscription Term Selected": selectedTerm,
    "Number of licences": licenseQty,
    "Subscription Tier (current tier)": currentTier,
    "Subscription Term (current term)": currentTerm,
  });
};

const MIXPANEL_EVENT_MAP: { [path: string]: string } = {
  "/": "Web Home Page View",
  "/pricing/": "Web Pricing Page View",
  "/free-trial/": "Web Registration Page View",
  "/login/": "Web Login Page View",
  "/sign-up/thanks/": "Web Signup Thanks Page View",
  "/download/": "Web Download Page View",
  "/using-videoscribe/": "Web Using VideoScribe Page View",
  "/whiteboard-animation/": "Web Whiteboard Animation Page View",
  "/videoscribe-for-charities/": "Web For Charities Page View",
  "/business/": "Web For Business Page View",
  "/education/": "Web For Education Page View",
  "/marketing/": "Web For Marketing Page View",
  "/installation-and-login/": "Web Installation & Login Page View",
  "/how-to-create-a-whiteboard-video/":
    "Web How to create a whiteboard video Page View",
  "/transitions-and-timings/": "Web Transitions & Timings Page View",
  "/templates/": "Web Template Library Page View",
  "/contact-us/": "Web Contact Us Page View",
  "/resellers/": "Web Resellers Page View",
  "/sign-up/please-confirm/": "Web Please Verify Email Page View",
  "/confirm-email/": "Web Verifying Email Holding Page View",
  "/pricing/purchase/videoscribe-yearly/":
    "Web Purchase Yearly Login Page View",
  "/pricing/purchase/videoscribe-yearly-region1/":
    "Web Purchase Yearly Region 1 Login Page View",
  "/pricing/purchase/videoscribe-monthly/":
    "Web Purchase Monthly Login Page View",
  "/checkout/thank-you/": "Web Checkout Thank You Page View",
  "/pricing/purchase/videoscribe-monthly-region1/":
    "Web Purchase Monthly Region 1 Login Page View",
  "/legal-professionals/": "Web For Legal Professionals Page View",
};

export const trackPageView = (pageExperiments: PageExperiment[] = []) => {
  const path = window.location.pathname.endsWith("/")
    ? window.location.pathname
    : `${window.location.pathname}/`;
  const eventName = MIXPANEL_EVENT_MAP[path];

  if (eventName) {
    const experimentEventProperties = pageExperiments.reduce(
      (acc, { experimentName, experimentBucket }) => {
        acc[experimentName] = experimentBucket;
        return acc;
      },
      {} as { [experimentName: string]: string },
    );

    trackMixpanelEvent(eventName, experimentEventProperties, {
      send_immediately: true,
      transport: "sendBeacon",
    });
  }
};
